import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Leaf, Snowflake, HeartHandshake } from 'lucide-react';
import Reveal from '../components/Reveal';
import { images, values } from '../mock';

const valueIcons = [Sparkles, Leaf, Snowflake, HeartHandshake];

const PageHero = ({ eyebrow, title, image }) => (
  <section className="relative h-[46vh] min-h-[340px] flex items-center justify-center overflow-hidden">
    <div className="absolute inset-0 bg-cover bg-center slide-zoom" style={{ backgroundImage: `url(${image})` }} />
    <div className="absolute inset-0 bg-gradient-to-b from-[#0f2c1f]/90 via-[#0f2c1f]/70 to-[#0f2c1f]/90" />
    <div className="relative z-10 text-center px-5 pt-16">
      <p className="animate-fade-up text-[#dcbf74] text-xs md:text-sm font-semibold tracking-[0.35em] mb-4">{eyebrow}</p>
      <h1 className="animate-fade-up font-serif-display text-white text-4xl md:text-6xl font-bold text-shadow-lg" style={{ animationDelay: '0.15s' }}>{title}</h1>
    </div>
  </section>
);

const About = () => {
  return (
    <div>
      <PageHero eyebrow="OUR LEGACY & MISSION" title="Our Heritage" image={images.spices2} />

      <section className="py-20 md:py-28 bg-[#faf7f0]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <div className="img-zoom-parent relative rounded-3xl overflow-hidden shadow-xl">
              <img src={images.market} alt="KST Spice Corner heritage" className="img-zoom w-full h-[460px] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-[#c8a24c] text-sm font-semibold tracking-[0.3em] mb-3 uppercase">Pioneering the Finest Grocery Standards</p>
            <h2 className="font-serif-display text-[#163a29] text-3xl md:text-4xl font-bold mb-6 leading-tight">
              Sydney's flagship destination for authentic groceries
            </h2>
            <p className="text-[#3a4a42] leading-relaxed mb-5">
              KST Spice Corner stands proudly as Sydney's ultimate flagship destination for authenticated Indian and Sri Lankan grocery distributions. Through careful oversight and sourcing protocols, we supply an elite lineup of raw spices, local farm greens, organic pulses, and premium imports.
            </p>
            <p className="text-[#3a4a42] leading-relaxed mb-8">
              By keeping quality and transparency at the absolute forefront, our customers enjoy custom house collections like <span className="font-semibold text-[#163a29]">Sinthia</span> and <span className="font-semibold text-[#163a29]">KST Spices</span>, meticulously curated to align with premium family cooking traditions across New South Wales.
            </p>
            <Link to="/brands" className="group inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#163a29] text-white font-semibold hover:bg-[#0f2c1f] transition-all duration-300">
              Discover Our Brands
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#163a29]">
        <div className="max-w-6xl mx-auto px-5 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { n: '15+', l: 'Years of Trust' },
            { n: '2', l: 'Flagship Stores' },
            { n: '300+', l: 'Premium Products' },
            { n: '10k+', l: 'Happy Families' },
          ].map((s, i) => (
            <Reveal key={s.l} delay={i * 80}>
              <p className="font-serif-display text-[#c8a24c] text-4xl md:text-5xl font-bold">{s.n}</p>
              <p className="text-white/70 text-sm tracking-wide mt-2">{s.l}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal className="text-center mb-14">
            <p className="text-[#c8a24c] text-sm font-semibold tracking-[0.3em] mb-3 uppercase">What Sets Us Apart</p>
            <h2 className="font-serif-display text-[#163a29] text-4xl md:text-5xl font-bold">Our Commitments</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => {
              const Icon = valueIcons[i];
              return (
                <Reveal key={v.title} delay={i * 90}>
                  <div className="h-full text-center bg-[#faf7f0] border border-[#e8e0cf] rounded-2xl p-8 lift-card">
                    <div className="w-16 h-16 mx-auto rounded-full bg-[#163a29] flex items-center justify-center mb-5">
                      <Icon className="text-[#c8a24c]" size={28} />
                    </div>
                    <h3 className="font-serif-display text-[#163a29] text-xl font-semibold mb-3">{v.title}</h3>
                    <p className="text-[#3a4a42] text-sm leading-relaxed">{v.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
